import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useExpenses } from "@/contexts/expense-context";
import { BottomNav } from "@/components/bottom-nav";
import type { CategoryViewModel, CreateCategoryDto } from "@/lib/domains";
import { CategoryService } from "@/lib/domains/category";
import { RecurringExpenseService } from "@/lib/domains/recurring";
import { DEFAULT_BASE_CURRENCY, formatCurrency } from "@/lib/utils/currency";
import { QuickEntryService } from "@/lib/domains/quick-entry";
import type { SmartDefaults, RecentExpense } from "@/lib/domains/quick-entry";
import { AmountInput } from "@/components/form/amount-input";
import { CategoryInput } from "@/components/form/category-input";
import { RecurringExpenseFields } from "@/components/form/recurring-expense-fields";
import { DescriptionInput } from "@/components/form/description-input";
import { VALIDATION_RULES, ERROR_MESSAGES } from "@/lib/constants";
import { handleError } from "@/lib/utils/error-handler";
import { useAddExpenseForm } from "./use-add-expense-form";

export default function AddExpensePage() {
    const navigate = useNavigate();
    const { addExpense, defaultCurrency, currentUser, refreshExpenses } = useExpenses();
    const userDefaultCurrency = defaultCurrency || DEFAULT_BASE_CURRENCY;

    // Refactored: Using useReducer via custom hook instead of 8+ useState calls
    const { state, actions } = useAddExpenseForm(userDefaultCurrency, "");

    const [categories, setCategories] = useState<CategoryViewModel[]>([]);
    const [recentCategoryIds, setRecentCategoryIds] = useState<string[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(true);

    // Smart defaults state
    const [smartDefaults, setSmartDefaults] = useState<SmartDefaults | null>(null);
    const [loadingDefaults, setLoadingDefaults] = useState(true);

    // Load categories and smart defaults on mount
    useEffect(() => {
        async function loadData() {
            if (!currentUser) return;

            try {
                setLoadingCategories(true);
                setLoadingDefaults(true);

                // Load categories and smart defaults in parallel
                const [cats, recent, defaults] = await Promise.all([
                    CategoryService.getVisibleCategoriesWithPresentation(currentUser.id, 'light'),
                    CategoryService.getRecentCategoriesWithPresentation(currentUser.id, 4, 'light'),
                    QuickEntryService.getSmartDefaults(currentUser.id, 'light'),
                ]);

                setCategories(cats);
                setRecentCategoryIds(recent.map(c => c.id));
                setSmartDefaults(defaults);

                // Set default category: suggested > first recent > first visible
                const defaultCategoryId =
                    defaults.suggestedCategory?.id ||
                    recent[0]?.id ||
                    cats[0]?.id ||
                    "";

                if (defaultCategoryId && !state.formData.categoryId) {
                    actions.setField('categoryId', defaultCategoryId);
                }
            } catch (error) {
                console.error('Failed to load data:', error);
            } finally {
                setLoadingCategories(false);
                setLoadingDefaults(false);
            }
        }

        loadData();
    }, [currentUser]);

    const handleCreateCategory = async (dto: CreateCategoryDto) => {
        if (!currentUser) return;

        // Create business data only
        const newCat = await CategoryService.createCategory(currentUser.id, dto);
        // Get it back with presentation
        const newCategoryWithPresentation = await CategoryService.getCategoryById(
            newCat.id,
            currentUser.id,
            'light'
        );
        if (newCategoryWithPresentation) {
            setCategories(prev => [...prev, newCategoryWithPresentation]);
            actions.setField('categoryId', newCategoryWithPresentation.id);
        }
    };

    const lastExpense = smartDefaults?.recentExpenses[0];

    // Handler for quick-adding similar expense
    const handleQuickAdd = (expense: RecentExpense) => {
        actions.setMultipleFields({
            amount: expense.amount.toString(),
            currency: expense.currency,
            date: new Date().toISOString().split("T")[0],
            categoryId: expense.category.id,
            description: expense.description || "",
            frequency: 'monthly',
            endDate: "",
        });
    };

    const handleSubmit = async (e: React.FormEvent, stayOnPage = false) => {
        e.preventDefault();
        actions.clearErrors();
        actions.setSubmitting(true);

        try {
            const amountValue = parseFloat(state.formData.amount);

            // Validation
            if (!state.formData.amount || amountValue <= 0) {
                actions.setError('amount', ERROR_MESSAGES.AMOUNT_REQUIRED);
                actions.setSubmitting(false);
                return;
            }

            if (amountValue > VALIDATION_RULES.MAX_AMOUNT) {
                actions.setError('amount', ERROR_MESSAGES.AMOUNT_TOO_LARGE);
                actions.setSubmitting(false);
                return;
            }

            if (!state.formData.categoryId) {
                actions.setError('category', ERROR_MESSAGES.CATEGORY_REQUIRED);
                actions.setSubmitting(false);
                return;
            }

            // Get category name for backward compatibility
            const category = categories.find(c => c.id === state.formData.categoryId);

            if (state.isRecurring) {
                // Create recurring expense template
                await RecurringExpenseService.createRecurringExpense({
                    amount: amountValue,
                    currency: state.formData.currency,
                    categoryId: state.formData.categoryId,
                    description: state.formData.description.trim() || undefined,
                    frequency: state.formData.frequency,
                    startDate: state.formData.date,
                    endDate: state.formData.endDate || undefined,
                });

                // If start date is today or past, generate the expense immediately
                const today = new Date().toISOString().split("T")[0];
                if (state.formData.date <= today) {
                    await RecurringExpenseService.generateDueExpenses();
                    // Refresh expense list to show the generated expense
                    await refreshExpenses();
                }
            } else {
                // Create one-time expense
                await addExpense({
                    amount: amountValue,
                    currency: state.formData.currency,
                    date: state.formData.date,
                    category: category?.displayName || 'Other', // Legacy field
                    categoryId: state.formData.categoryId, // New field
                    description: state.formData.description.trim() || undefined,
                });
            }

            actions.resetForm(userDefaultCurrency, categories[0]?.id || "");
            if (!stayOnPage) {
                navigate("/expenses");
            }
        } catch (err: unknown) {
            actions.setError('amount', handleError(err, 'Failed to add expense. Please try again.'));
        } finally {
            actions.setSubmitting(false);
        }
    };

    const handleSubmitAndStay = async (e: React.MouseEvent<HTMLButtonElement>) => {
        await handleSubmit(e as unknown as React.FormEvent, true);
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-4xl mx-auto px-4 py-6">
                    <h1 className="text-3xl font-bold text-gray-900">Add Expense</h1>
                </div>
            </div>

            {/* Form */}
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Recent Expenses Panel */}
                {!loadingDefaults && smartDefaults && smartDefaults.recentExpenses.length > 0 && (
                    <div className="mb-6 max-w-md mx-auto">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-sm font-medium text-gray-700">Quick Add Similar</h2>
                            {lastExpense && (
                                <button
                                    type="button"
                                    onClick={() => handleQuickAdd(lastExpense)}
                                    className="text-sm text-teal-700 font-medium hover:text-teal-800"
                                >
                                    Same as last
                                </button>
                            )}
                        </div>
                        <div className="space-y-2">
                            {smartDefaults.recentExpenses.slice(0, 3).map((expense) => (
                                <button
                                    key={expense.id}
                                    type="button"
                                    onClick={() => handleQuickAdd(expense)}
                                    className="w-full bg-white border border-gray-200 rounded-lg p-3 hover:border-teal-500 hover:bg-teal-50 transition-colors text-left flex items-center justify-between"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-xl">{expense.category.icon}</span>
                                        <div>
                                            <div className="font-medium text-gray-900">
                                                {expense.category.displayName}
                                            </div>
                                            {expense.description && (
                                                <div className="text-sm text-gray-500">
                                                    {expense.description}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="font-semibold text-gray-900">
                                        {formatCurrency(expense.amount, expense.currency)}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md mx-auto">
                    <AmountInput
                        amount={state.formData.amount}
                        currency={state.formData.currency}
                        onAmountChange={(amount) => {
                            actions.setField('amount', amount);
                            if (state.errors.amount) actions.clearError('amount');
                        }}
                        onCurrencyChange={(currency) => actions.setField('currency', currency)}
                        error={state.errors.amount}
                        quickAmounts={smartDefaults?.recentAmounts}
                        onQuickAmountSelect={(amount) => {
                            actions.setField('amount', amount.toString());
                            if (state.errors.amount) actions.clearError('amount');
                        }}
                    />

                    {/* Date */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Date *
                        </label>
                        <input
                            type="date"
                            value={state.formData.date}
                            onChange={(e) => actions.setField('date', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                            required
                        />
                    </div>

                    <CategoryInput
                        categories={categories}
                        selectedCategoryId={state.formData.categoryId}
                        recentCategoryIds={recentCategoryIds}
                        onSelect={(categoryId) => {
                            actions.setField('categoryId', categoryId);
                            if (state.errors.category) actions.clearError('category');
                        }}
                        onCreateCategory={handleCreateCategory}
                        error={state.errors.category}
                        suggestedCategory={smartDefaults?.suggestedCategory ?? undefined}
                        loadingCategories={loadingCategories}
                    />

                    {/* Recurring Expense Toggle */}
                    <div className="mb-6 border-t border-gray-200 pt-6">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={state.isRecurring}
                                onChange={(e) => actions.setRecurring(e.target.checked)}
                                className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                            />
                            <div>
                                <div className="text-sm font-medium text-gray-900">Make this recurring</div>
                                <div className="text-xs text-gray-500">Automatically create this expense on a schedule</div>
                            </div>
                        </label>

                        {state.isRecurring && (
                            <div className="mt-4 space-y-4 pl-8">
                                <RecurringExpenseFields
                                    frequency={state.formData.frequency}
                                    endDate={state.formData.endDate}
                                    minEndDate={state.formData.date}
                                    onFrequencyChange={(frequency) => actions.setField('frequency', frequency)}
                                    onEndDateChange={(endDate) => actions.setField('endDate', endDate)}
                                />

                                {/* Info */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                    <div className="flex gap-2">
                                        <span className="text-blue-600 flex-shrink-0">ℹ️</span>
                                        <div className="text-sm text-blue-800">
                                            <p className="font-medium mb-1">Auto-generated expenses represent money you've already spent</p>
                                            <p className="text-blue-700">
                                                Perfect for subscriptions and auto-paid bills. Expenses will appear in your list on their due date (starting {state.formData.date}).
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <DescriptionInput
                        value={state.formData.description}
                        onChange={(description) => actions.setField('description', description)}
                    />

                    {/* Buttons */}
                    <div className="flex gap-3 flex-wrap">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="flex-1 min-w-[140px] px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={state.isSubmitting}
                            onClick={handleSubmitAndStay}
                            className="flex-1 min-w-[140px] px-4 py-2 text-teal-700 bg-teal-50 hover:bg-teal-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                        >
                            {state.isSubmitting ? "Adding..." : "Add & stay"}
                        </button>
                        <button
                            type="submit"
                            disabled={state.isSubmitting}
                            className="flex-1 min-w-[140px] px-4 py-2 text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                        >
                            {state.isSubmitting ? "Adding..." : "Add & view"}
                        </button>
                    </div>
                </form>
            </div>

            {/* Bottom Navigation */}
            <BottomNav />
        </div>
    );
}
